import makeStyles from '@mui/styles/makeStyles';
import { useDispatch, useSelector } from 'react-redux';
import { ViewerToolbar } from './viewerToolbar';
import * as React from 'react';
import _ from 'lodash';
import { activeViewerWindow, setCurrentToolbarSet, setCurrentPageSet } from './store/documentViewerReducer';
import { ViewerTabs } from './viewerTabs';
import { ViewerDocument } from './viewerDocument';
import { Stack, Box, IconButton } from '@mui/material';
import { getDocPageId, getTabViewerWindowId } from './store/methods';
import { INIT_PAGE_PROPERTY, INIT_TAB_TOOLBAR } from './store/constans';
import { BookmarkPage, Comments, PagesThumbnails } from './assets/icons';
import { THUMBNAIL_FILTER } from './store/enums';
import { ThumbnailPage } from './elements/thumbnailPage';
import { AnnotationReactItem } from './elements/annotationListItem';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        flexGrow: 1,
        justifyContent: 'space-between',
        height: '100%',
        paddingLeft: '2px',
        paddingRight: '2px',
    },
    toolbarAdditionalPanel: {
        width: '200px',
        borderLeft: `1px solid ${theme.palette.documentViewer.timelineBorderColor}`,
        '& ::-webkit-scrollbar-thumb:hover': {
            background: `${theme.palette.documentViewer.lowContrastTextHoverColor} !important`,
        },
        '& ::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
        },
        '& ::-webkit-scrollbar': {
            width: '5px',
            height: '5px',
        },
        '& ::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.documentViewer.lowContrastTextColor,
        },
    },
    toolbarRightSide: {
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        position: 'fixed',
        right: '0px',
        backgroundColor: theme.palette.documentViewer.toolPanelBackground,
    },
    wideViewFilters: {
        borderRadius: '2px',
        padding: '10px',
        '&.selected': {
            color: theme.palette.primary.main,
        },
        '&.contained': {
            backgroundColor: theme.palette.primary.main,
        },
    },
    pages: {
        alignItems: 'center',
        overflowY: 'auto',
        height: 'calc(100% - 45px);',
        paddingBottom: '15px',
    },
}));

export function DocumentView(props) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const thumbnailPanelRef = React.useRef();
    const rightPanelRef = React.useRef();
    const viewerRef = React.useRef();
    const [viewerWidth, setViewerWidth] = React.useState(0);
    const [viewerHeight, setViewerHeight] = React.useState(0);
    const activeViewerWindowIdx = useSelector(activeViewerWindow);

    const wideViewSubData = useSelector((state) => _.get(state, `documentViewer.currentToolbarSet.wideViewSubData`, ''));
    const [wideView, setWideView] = React.useState(wideViewSubData !== '');
    const [toolbarsState, setToolbarsState] = React.useState({}); //maybe redux to store it or session storage or leave it as it is?
    const [currentToolbarSetId, setCurrentToolbarSetId] = React.useState('');
    const currentToolbarSet = useSelector((state) => _.get(state, `documentViewer.currentToolbarSet`, INIT_TAB_TOOLBAR));
    const [pagesState, setPagesState] = React.useState({});
    const [currentPageSetId, setCurrentPageSetId] = React.useState('');
    const currentPageSet = useSelector((state) => _.get(state, `documentViewer.currentDocPageSet`, INIT_PAGE_PROPERTY));
    const activePage = useSelector((state) => _.get(state, `documentViewer.currentToolbarSet.activePage`, 0));

    const [filterThumbnails, setFilterThumbnails] = React.useState(THUMBNAIL_FILTER.ALL);
    const [currentDocumentId, setCurrentDocumentId] = React.useState('');

    React.useEffect(() => {
        if (viewerRef.current && rightPanelRef.current) {
            setViewerWidth(viewerRef.current.getBoundingClientRect().width - rightPanelRef.current.getBoundingClientRect().width);
            setViewerHeight(viewerRef.current.getBoundingClientRect().height - 40);
        }
    }, [viewerRef, rightPanelRef, wideView]);

    React.useEffect(() => {
        if (props.selectedTabs && props.selectedTabs.length > activeViewerWindowIdx) {
            rightPanelRef.current.style.display = 'flex';
            const selectedTabId = props.selectedTabs[activeViewerWindowIdx];
            const tab = _.find(props.openTabs, (t) => t.tabId === selectedTabId);
            if (tab) {
                const toolbarId = getTabViewerWindowId(tab.tabId, activeViewerWindowIdx);
                setCurrentToolbarSetId((prevToolbarId) => {
                    if (prevToolbarId !== toolbarId) {
                        setToolbarsState((prevToolbarSets) => {
                            let tmpToolbarSets = _.cloneDeep(prevToolbarSets);
                            tmpToolbarSets[prevToolbarId] = _.cloneDeep(currentToolbarSet);
                            if (!tmpToolbarSets.hasOwnProperty(toolbarId)) {
                                tmpToolbarSets[toolbarId] = _.cloneDeep(INIT_TAB_TOOLBAR);
                                tmpToolbarSets[toolbarId].toolbarId = toolbarId;
                                tmpToolbarSets[toolbarId].activePage = 1;
                                tmpToolbarSets[toolbarId].pageCount = _.get(tab, 'documentData.metadata.pageCount', 0);
                                tmpToolbarSets[toolbarId].allBookmarks = _.get(tab, 'documentData.bookmarks', []);
                                tmpToolbarSets[toolbarId].allAnnotations = _.get(tab, 'documentAnnotations', []);
                            }
                            return tmpToolbarSets;
                        });
                    }
                    return toolbarId;
                });
                const pageId = getDocPageId(tab.documentData.id, activePage < 1 ? 1 : activePage, activeViewerWindowIdx);
                setCurrentPageSetId((prevPageSetId) => {
                    if (prevPageSetId !== pageId) {
                        setPagesState((prevPagesState) => {
                            let tmpPagesState = _.cloneDeep(prevPagesState);
                            if (prevPageSetId !== '' && prevPageSetId === currentPageSet.docPageId)
                                tmpPagesState[prevPageSetId] = _.cloneDeep(currentPageSet);
                            if (!tmpPagesState.hasOwnProperty(pageId)) {
                                tmpPagesState[pageId] = _.cloneDeep(INIT_PAGE_PROPERTY);
                                tmpPagesState[pageId].docPageId = pageId;
                            }
                            return tmpPagesState;
                        });
                    }
                    return pageId;
                });
                if (currentDocumentId !== tab.documentData.id) {
                    setCurrentDocumentId(tab.documentData.id);
                }
            } else {
                setCurrentToolbarSetId('');
                setCurrentPageSetId('');
                rightPanelRef.current.style.display = 'none';
            }
        }
    }, [props.selectedTabs, activeViewerWindowIdx, activePage]);

    React.useEffect(() => {
        if (currentToolbarSetId !== '' && toolbarsState[currentToolbarSetId]) {
            dispatch(setCurrentToolbarSet(_.cloneDeep(toolbarsState[currentToolbarSetId])));
        } else {
            dispatch(setCurrentToolbarSet(_.cloneDeep(INIT_TAB_TOOLBAR)));
        }
    }, [currentToolbarSetId]);

    React.useEffect(() => {
        if (currentPageSetId !== '' && pagesState.hasOwnProperty(currentPageSetId)) {
            dispatch(setCurrentPageSet(_.cloneDeep(pagesState[currentPageSetId])));
        } else {
            dispatch(setCurrentPageSet(_.cloneDeep(INIT_PAGE_PROPERTY)));
        }
    }, [currentPageSetId]);

    React.useEffect(() => {
        setWideView(wideViewSubData !== '');
    }, [wideViewSubData]);

    return (
        <Box className={classes.root} id="document-viewer" ref={viewerRef}>
            <Stack style={{ width: '100%' }}>
                <ViewerTabs
                    selectedTabs={props.selectedTabs}
                    openTabs={props.openTabs}
                    closeTab={props.closetab}
                    changeActiveTab={props.changeActiveTabId}
                />
                <ViewerDocument maxWidth={viewerWidth} maxHeight={viewerHeight} viewPortWidth={viewerWidth} viewPortHeight={viewerHeight} />
            </Stack>
            <div className={classes.toolbarRightSide} ref={rightPanelRef}>
                {wideView && (
                    <Box className={classes.toolbarAdditionalPanel}>
                        {wideViewSubData === 'pages' && (
                            <>
                                <Stack direction={'row'} gap={'1px'} justifyContent={'flex-start'}>
                                    <IconButton
                                        className={`${classes.wideViewFilters} ${filterThumbnails === THUMBNAIL_FILTER.ALL ? 'selected' : ''}`}
                                        onClick={() => setFilterThumbnails(THUMBNAIL_FILTER.ALL)}
                                    >
                                        <PagesThumbnails />
                                    </IconButton>
                                    <IconButton
                                        className={`${classes.wideViewFilters} ${filterThumbnails === THUMBNAIL_FILTER.BOOKMARKED ? 'selected' : ''}`}
                                        onClick={() =>
                                            setFilterThumbnails((prevFilter) =>
                                                prevFilter === THUMBNAIL_FILTER.BOOKMARKED
                                                    ? THUMBNAIL_FILTER.ALL
                                                    : THUMBNAIL_FILTER.BOOKMARKED,
                                            )
                                        }
                                    >
                                        <BookmarkPage />
                                    </IconButton>
                                    <IconButton
                                        className={`${classes.wideViewFilters} ${filterThumbnails === THUMBNAIL_FILTER.COMMENTED ? 'selected' : ''}`}
                                        onClick={() =>
                                            setFilterThumbnails((prevFilter) =>
                                                prevFilter === THUMBNAIL_FILTER.COMMENTED
                                                    ? THUMBNAIL_FILTER.ALL
                                                    : THUMBNAIL_FILTER.COMMENTED,
                                            )
                                        }
                                    >
                                        <Comments />
                                    </IconButton>
                                </Stack>
                                <Stack
                                    direction={'column'}
                                    gap={'16px'}
                                    className={classes.pages}
                                    paddingX={'10px'}
                                    height={'100%'}
                                    ref={thumbnailPanelRef}
                                >
                                    {currentToolbarSet &&
                                        thumbnailPanelRef &&
                                        thumbnailPanelRef.current &&
                                        currentToolbarSet.pageCount &&
                                        filterThumbnails === THUMBNAIL_FILTER.ALL &&
                                        [...Array(currentToolbarSet.pageCount).keys()].map((page) => {
                                            return (
                                                <ThumbnailPage
                                                    page={page + 1}
                                                    documentId={currentDocumentId}
                                                    filter={filterThumbnails}
                                                    key={`page-thumbnail-${page}`}
                                                    parentRef={thumbnailPanelRef}
                                                />
                                            );
                                        })}
                                    {currentToolbarSet &&
                                        thumbnailPanelRef &&
                                        thumbnailPanelRef.current &&
                                        filterThumbnails === THUMBNAIL_FILTER.BOOKMARKED &&
                                        currentToolbarSet.allBookmarks.map((bkm) => {
                                            return (
                                                <ThumbnailPage
                                                    page={bkm.pageNumber}
                                                    documentId={currentDocumentId}
                                                    filter={filterThumbnails}
                                                    key={`page-bookmarked-thumbnail-${bkm.pageNumber}`}
                                                    parentRef={thumbnailPanelRef}
                                                />
                                            );
                                        })}

                                    {currentToolbarSet &&
                                        filterThumbnails === THUMBNAIL_FILTER.COMMENTED &&
                                        currentToolbarSet.allAnnotations.map((annotation) => <AnnotationReactItem {...annotation} />)}
                                </Stack>
                            </>
                        )}
                        {wideViewSubData !== 'pages' && <div>{wideViewSubData}</div>}
                    </Box>
                )}
                <ViewerToolbar selectedTabs={props.selectedTabs} />
            </div>
        </Box>
    );
}
